import React from 'react'

import { Maybe, ObjectHash } from '../../../types/shared'
import ChartLayout from '../../models/chart_layout'
import MouseModel from '../../models/mouse'
import fetchApi, { getFormDataFromObject } from '../../utils/fetch_api'
import { chartLayoutToImg } from './chartLayoutToImg'

interface ImgData {
  imgUrl: string
  shareUrl: string
  ticker: string
}

interface DataState {
  isLoading: boolean
  data: ImgData | null
  error: ObjectHash | null
}

const imgFromUrlToBase64 = (url: string): Promise<string | null> =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string | null)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
    )

interface UseImageGeneratorProps {
  layoutModel?: Maybe<ChartLayout>
  imgUrl?: Maybe<string>
  ticker?: Maybe<string>
  timeframe?: Maybe<string>
}

export const useImageGenerator = ({ layoutModel, imgUrl, ticker, timeframe }: UseImageGeneratorProps) => {
  const [state, setState] = React.useState<DataState>({ isLoading: false, data: null, error: null })

  const convertChartsToImage = React.useCallback(
    async ({
      getDataUrl,
      ticker,
      timeframe,
    }: {
      getDataUrl: () => string | null | Promise<string | null>
      ticker: string
      timeframe: string
    }) => {
      try {
        setState((prevState) => ({
          ...prevState,
          isLoading: true,
        }))
        const dataUrl = await getDataUrl()
        const data = await fetchApi<ImgData>({
          location: '/publish_idea_submit.ashx',
          method: 'POST',
          body: getFormDataFromObject({
            file: dataUrl?.split('base64,')[1],
            ticker: ticker,
            period: timeframe,
          }),
        })
        setState((prevState) => ({
          ...prevState,
          data,
        }))
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          error: error as ObjectHash,
        }))
      } finally {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }))
      }
    },
    []
  )

  React.useEffect(() => {
    if (imgUrl && ticker && timeframe) {
      convertChartsToImage({ getDataUrl: () => imgFromUrlToBase64(imgUrl), ticker, timeframe })
    } else if (layoutModel) {
      layoutModel.updateAttributes({
        scrollable: layoutModel.initialScrollable,
        isTouchCrossActive: false,
      })
      MouseModel.updateAttributes({
        position: null,
        pane: null,
      })
      const quote = layoutModel.activeChart.quote()
      convertChartsToImage({
        getDataUrl: () => chartLayoutToImg(layoutModel),
        ticker: quote.ticker,
        timeframe: quote.timeframe,
      })
    }
  }, [layoutModel, imgUrl, ticker, timeframe, convertChartsToImage])

  return state
}
